import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { get, post } from '../api';
import { askConfirm } from '../components/ask';
import { ComposeButtons } from '../components/ComposeButtons';
import { Problems } from '../components/Field';
import { FilterChips } from '../components/FilterChips';
import { Row } from '../components/FormRow';
import { currentLocale } from '../dates';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { formatMoney } from '../money';
import { useMoneyFilter } from './moneyFilter';

interface Invoice {
  id: number;
  /** What it is called: Xero's number, else the business's own (INV-0012). */
  label: string;
  billed_to_name: string;
  reference: string;
  description: string;
  status: string;
  subtotal: string;
  tax_total: string;
  xero_number: string;
  xero_invoice_id: string;
  period_start: string | null;
  period_end: string | null;
  sent_at: string | null;
  paid_at: string | null;
  due_days: number;
  created_at: string;
  xero_synced_at: string | null;
}
interface SyncReport {
  connected: boolean;
  checked: number;
  updated: number;
  error: string;
}
interface Line {
  id: number;
  visit_id: number | null;
  description: string;
  quantity: string;
  amount: string;
  is_cancelled: boolean;
}

/**
 * One sequence for every invoice, Draft → Issued → Paid (or Overdue, Voided), whether Xero
 * moves it along or the office does. A draft in Xero ("sent" on the server) is still a draft.
 */
const STAGE: Record<string, { label: string; pill: string }> = {
  draft: { label: 'Draft', pill: 'pill warn' },
  issued: { label: 'Issued', pill: 'pill info' },
  overdue: { label: 'Overdue', pill: 'pill bad' },
  paid: { label: 'Paid', pill: 'pill ok' },
  voided: { label: 'Voided', pill: 'pill' },
};
const CHIP: [string, string][] = [
  ['draft', 'Draft'],
  ['issued', 'Issued'],
  ['overdue', 'Overdue'],
  ['paid', 'Paid'],
  ['voided', 'Voided'],
  ['', 'All'],
];

const RARE = ['overdue', 'voided'];

/** What an invoice kept in this app says when printed. */
interface InvoiceDocument {
  label: string;
  issued: string;
  due: string;
  business: { name: string; logo: string; abn: string; address: string; phone: string };
  bill_to: { name: string; email: string; address: string };
  bank: { name: string; bsb: string; account: string } | null;
  tax_invoice: boolean;
  /** Details the business hasn't filled in yet, without which the invoice is incomplete. */
  missing: string[];
}
interface Loaded {
  invoice: Invoice;
  lines: Line[];
  xero_url?: string;
  connected: boolean;
  document?: InvoiceDocument;
  email?: { to: string; href: string; body: string } | null;
}

type Dated = Pick<Invoice, 'status' | 'sent_at' | 'due_days'>;

/** When the client is due to pay: the day it was sent plus its payment terms. */
// shortcut: for a Xero invoice sent_at is when its draft reached Xero, not when it was approved there; store Xero's DueDate in the sync if the two drift apart.
export function dueDate(inv: Dated): Date | null {
  if (!inv.sent_at) return null;
  const d = new Date(inv.sent_at);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + inv.due_days);
  return d;
}

/** Where an invoice stands in the one sequence, as of today. */
export function stageOf(inv: Dated, now = new Date()): string {
  if (inv.status === 'draft' || inv.status === 'sent') return 'draft';
  if (inv.status !== 'authorised') return inv.status;
  const due = dueDate(inv);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return due && due < today ? 'overdue' : 'issued';
}

/** Xero holds it (or was told its number): its status follows Xero. */
const inXero = (inv: Pick<Invoice, 'xero_invoice_id' | 'xero_number'>) =>
  inv.xero_invoice_id !== '' || inv.xero_number.trim() !== '';

/** Who moves the invoice along: Xero, or the office here. */
const keptIn = (inv: Invoice, connected: boolean) =>
  inXero(inv) ? 'Xero' : connected ? 'Not in Xero yet' : 'In app';

function StagePill(props: { inv: Invoice; connected: boolean }) {
  const s = STAGE[stageOf(props.inv)] ?? { label: props.inv.status, pill: 'pill' };
  return (
    <>
      <span className={s.pill}>{s.label}</span>{' '}
      <span className="muted source">{keptIn(props.inv, props.connected)}</span>
    </>
  );
}

const money = formatMoney;
/** What the client pays: the total with its GST. */
const total = (inv: Pick<Invoice, 'subtotal' | 'tax_total'>) =>
  (Math.round(Number(inv.subtotal) * 100) + Math.round(Number(inv.tax_total) * 100)) / 100;
const day = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(currentLocale(), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';
const synced = (iso: string) =>
  new Date(iso).toLocaleString(currentLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

/** What each filter holds, said when it holds nothing. */
const EMPTY: Record<string, string> = {
  draft:
    'No drafts. An invoice starts as a draft when you create it from the Visits tab; one in Xero stays a draft until it is approved there.',
  issued: 'No issued invoices waiting for payment.',
  overdue: 'Nothing overdue.',
  paid: 'No paid invoices yet.',
  voided: 'No voided invoices.',
  '': 'No sales invoices yet. Create one from the Visits tab.',
};

/**
 * The invoices sent to clients for work done: money coming in. With Xero, each one is
 * created there from the Visits tab, and approved, sent and paid there; this tab
 * reflects what Xero says, via its webhooks, the hourly check and Refresh. Without
 * Xero, the office marks each one sent and paid here.
 */
export function InvoicesTab() {
  const [tick, setTick] = useState(0);
  const [checking, setChecking] = useState(false);
  const [report, setReport] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const list = useLoad(
    () => get<{ items: Invoice[]; connected: boolean }>('/api/invoices?limit=100'),
    [tick],
  );
  const connected = list.data?.connected ?? false;

  // Refresh asks Xero about the open invoices first, so a change made there (an approval, a
  // payment, a deletion) shows now rather than at the next hourly check.
  async function refresh() {
    setChecking(true);
    setReport('');
    setProblems([]);
    try {
      const r = await post<SyncReport>('/api/invoices/sync');
      if (r.error) setProblems([`Xero did not answer: ${r.error}`]);
      else if (r.connected) {
        setReport(
          r.updated > 0
            ? `Checked with Xero: ${r.updated} ${r.updated === 1 ? 'invoice' : 'invoices'} updated.`
            : 'Checked with Xero: everything is up to date.',
        );
      }
    } catch (err) {
      setProblems(describe(err).problems);
    } finally {
      setChecking(false);
      setTick((t) => t + 1);
    }
  }
  const items = Array.isArray(list.data?.items) ? list.data!.items : [];
  const stages = new Map(items.map((i) => [i.id, stageOf(i)]));
  const counts = Object.fromEntries(
    CHIP.map(([k]) => [k, k ? items.filter((i) => stages.get(i.id) === k).length : items.length]),
  );
  // With no filter chosen, open on the most urgent step that has something waiting.
  const first = ['overdue', 'draft', 'issued'].find((k) => counts[k]! > 0) ?? '';
  const [chip, setChip] = useMoneyFilter('invoices', first);
  const shown = chip ? items.filter((i) => stages.get(i.id) === chip) : items;
  const withReference = shown.some((i) => i.reference.trim() !== '');
  const lastSynced = items
    .map((i) => i.xero_synced_at)
    .filter((s): s is string => !!s)
    .sort()
    .at(-1);
  return (
    <div className="money-tab">
      <div className="list-bar">
        <FilterChips
          label="Show"
          current={chip}
          chips={CHIP.filter(
            // Overdue and Voided are the exceptions, so they show only when something is in them.
            ([key]) => !RARE.includes(key) || counts[key]! > 0 || chip === key,
          ).map(([key, label]) => ({
            key,
            label,
            count: counts[key],
            alert: key === 'overdue',
          }))}
          onPick={setChip}
        />
        {connected && (
          <div className="bar-action">
            {lastSynced && <span className="muted">Xero last reported {synced(lastSynced)}</span>}
            <button
              type="button"
              className="quiet-button button"
              disabled={checking}
              onClick={() => void refresh()}
            >
              {checking ? 'Checking Xero…' : 'Refresh'}
            </button>
          </div>
        )}
      </div>
      <Problems problems={problems} title="Could not check Xero." />
      {report && (
        <p className="notice" role="status">
          {report}
        </p>
      )}
      {list.error && (
        <p role="alert" className="error">
          {list.error}
        </p>
      )}
      {list.loading && !list.data && <p className="muted">Loading…</p>}
      {list.data && shown.length === 0 && <p className="empty">{EMPTY[chip] ?? EMPTY['']}</p>}
      {shown.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Billed to</th>
                {withReference && <th>Reference</th>}
                <th>Status</th>
                <th className="num">Total ex GST</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((i) => {
                const due = dueDate(i);
                const late = stages.get(i.id) === 'overdue';
                return (
                  <tr key={i.id}>
                    <td>
                      <Link to={`/invoices/${i.id}`}>{i.label}</Link>
                    </td>
                    <td>{i.billed_to_name}</td>
                    {withReference && <td>{i.reference}</td>}
                    <td className="status">
                      <StagePill inv={i} connected={connected} />
                    </td>
                    <td className="num">{money(i.subtotal)}</td>
                    <td className={late ? 'late' : undefined}>
                      {/* Only an invoice still waiting to be paid has a due date worth showing. */}
                      {due && ['issued', 'overdue'].includes(stages.get(i.id)!)
                        ? day(due.toISOString())
                        : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** One invoice: its lines, and (while it is still a draft) taking a visit off or discarding it. */
export function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [tick, setTick] = useState(0);
  const [problems, setProblems] = useState<string[]>([]);
  // A page that sent us here (creating the invoice) can say what it did.
  const [message, setMessage] = useState(
    (location.state as { message?: string } | null)?.message ?? '',
  );
  const [undoPaid, setUndoPaid] = useState(false);
  const [number, setNumber] = useState('');
  const loaded = useLoad(() => get<Loaded>(`/api/invoices/${id}`), [id, tick]);

  async function act(run: () => Promise<unknown>, done: string, then?: () => void) {
    setProblems([]);
    setMessage('');
    setUndoPaid(false);
    try {
      await run();
      setMessage(done);
      if (then) then();
      else setTick((t) => t + 1);
    } catch (e) {
      setProblems(describe(e).problems);
    }
  }

  if (loaded.loading && !loaded.data) return <p>Loading…</p>;
  if (!loaded.data) {
    return (
      <>
        <p role="alert" className="error">
          {loaded.error}
        </p>
        <Link to="/money?tab=invoices">Back to invoices</Link>
      </>
    );
  }
  const { invoice: inv, lines, xero_url, connected, document: doc, email } = loaded.data;
  // Only a draft can be changed; once it has gone to Xero it is changed there.
  const draft = inv.status === 'draft';
  const xero = inXero(inv);
  const stage = stageOf(inv);
  const due = dueDate(inv);
  const owing = ['issued', 'overdue'].includes(stage);

  const markPaid = () =>
    void askConfirm({
      title: `Mark ${inv.billed_to_name}'s invoice paid?`,
      body: `Record that ${money(total(inv))} (including GST) has come in, in full.`,
      confirmLabel: 'Mark paid',
    }).then((yes) => {
      if (yes)
        void act(() => post(`/api/invoices/${inv.id}/paid`), 'Marked paid.').then(() =>
          setUndoPaid(true),
        );
    });

  return (
    <div className="stack narrow">
      <p>
        <Link to="/money?tab=invoices">← Invoices</Link>
      </p>
      <div className="row start">
        <h1>
          {xero || connected
            ? inv.xero_number
              ? `Invoice ${inv.xero_number}`
              : `Draft invoice #${inv.id}`
            : `Invoice ${inv.label}`}
        </h1>
        <StagePill inv={inv} connected={connected} />
        {xero_url && (
          <a className="button" href={xero_url} target="_blank" rel="noopener noreferrer">
            Open in Xero<span className="sr-only"> (opens a new tab)</span>
          </a>
        )}
      </div>
      <Problems problems={problems} title="Could not do that." />
      {message && (
        <p className="notice row start" role="status">
          <span>{message}</span>
          {undoPaid && inv.status === 'paid' && (
            <button
              type="button"
              className="quiet"
              onClick={() =>
                void act(
                  () => post(`/api/invoices/${inv.id}/unpaid`),
                  'Marked not paid. It is waiting to be paid again.',
                )
              }
            >
              Undo
            </button>
          )}
        </p>
      )}
      <section className="panel facts" aria-label="Invoice">
        <Row label="Billed to">
          <span>{inv.billed_to_name}</span>
        </Row>
        <Row label="Kept in">
          <span>
            {xero
              ? 'Xero. It is approved, sent and paid there, and its status here follows Xero.'
              : connected
                ? 'Not in Xero yet. Send it to Xero to go on.'
                : 'This app only. Mark it sent when it goes to the client, and paid when the money comes in.'}
          </span>
        </Row>
        {inv.reference && (
          <Row label="Reference">
            <span>{inv.reference}</span>
          </Row>
        )}
        {inv.description && (
          <Row label="Description">
            <span>{inv.description}</span>
          </Row>
        )}
        {inv.sent_at && (
          <Row label={xero ? 'Sent to Xero' : 'Sent to client'}>
            <span>{day(inv.sent_at)}</span>
          </Row>
        )}
        {due && owing && (
          <Row label="Due">
            <span className={stage === 'overdue' ? 'late' : undefined}>
              {day(due.toISOString())}
              {stage === 'overdue' && ' (overdue)'}
            </span>
          </Row>
        )}
        {inv.paid_at && (
          <Row label="Paid">
            <span>{day(inv.paid_at)}</span>
          </Row>
        )}
      </section>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th className="num">Amount</th>
              {draft && (
                <th>
                  <span className="sr-only">Take off</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <td>
                  {l.visit_id ? (
                    <Link to={`/visits/${l.visit_id}`}>{l.description}</Link>
                  ) : (
                    l.description
                  )}
                </td>
                <td className="num">{money(l.amount)}</td>
                {draft && (
                  <td className="actions">
                    <button
                      type="button"
                      className="quiet"
                      onClick={() =>
                        void act(
                          () => post(`/api/invoice-lines/${l.id}/remove`, { action: 'hold' }),
                          'Taken off. It will be ready to go on a later invoice.',
                        )
                      }
                    >
                      Hold for later<span className="sr-only"> {l.description}</span>
                    </button>
                    <button
                      type="button"
                      className="quiet"
                      onClick={() => {
                        void askConfirm({
                          title: 'Waive this visit?',
                          body: 'It will not be invoiced at all.',
                          confirmLabel: 'Waive',
                          danger: true,
                        }).then((yes) => {
                          if (yes) {
                            void act(
                              () => post(`/api/invoice-lines/${l.id}/remove`, { action: 'waive' }),
                              'Taken off and waived.',
                            );
                          }
                        });
                      }}
                    >
                      Waive<span className="sr-only"> {l.description}</span>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Total ex GST</th>
              <td className="num">{money(inv.subtotal)}</td>
              {draft && <td />}
            </tr>
            <tr>
              <th>GST</th>
              <td className="num">{money(inv.tax_total)}</td>
              {draft && <td />}
            </tr>
            <tr className="grand">
              <th>Client pays</th>
              <td className="num">
                <strong>{money(total(inv))}</strong>
              </td>
              {draft && <td />}
            </tr>
          </tfoot>
        </table>
      </div>

      {doc && (draft || owing) && (
        <SendIt
          id={inv.id}
          doc={doc}
          email={email ?? null}
          draft={draft}
          onSent={() =>
            void act(
              () => post(`/api/invoices/${inv.id}/issue`),
              `Marked sent to the client. It is due in ${inv.due_days} days.`,
            )
          }
        />
      )}

      {!xero && owing && !connected && (
        <div className="row start">
          <button type="button" onClick={markPaid}>
            Mark paid
          </button>
        </div>
      )}

      {draft && connected && (
        <form
          className="row start"
          onSubmit={(e) => {
            e.preventDefault();
            void act(
              () => post(`/api/invoices/${inv.id}/mark-sent`, { number }),
              `Recorded as ${number.trim()} in Xero. Its status follows Xero from now.`,
            );
          }}
        >
          <label>
            Already in Xero? Its number{' '}
            <input value={number} onChange={(e) => setNumber(e.target.value)} size={12} />
          </label>
          <button type="submit" className="quiet-button" disabled={number.trim() === ''}>
            Record number
          </button>
        </form>
      )}

      {draft && (
        <div className="row start">
          {connected ? (
            <button
              type="button"
              onClick={() =>
                void act(
                  () => post(`/api/invoices/${inv.id}/send`),
                  'Sent to Xero as a draft. Approve and send it to the client there.',
                )
              }
            >
              Send to Xero
            </button>
          ) : (
            <button
              type="button"
              onClick={() =>
                void act(
                  () => post(`/api/invoices/${inv.id}/issue`),
                  `Marked sent to the client. It is due in ${inv.due_days} days.`,
                )
              }
            >
              Mark sent to client
            </button>
          )}
          <button
            type="button"
            className="danger"
            onClick={() => {
              void askConfirm({
                title: 'Discard this draft?',
                body: 'Its visits go back to the work to invoice.',
                confirmLabel: 'Discard draft',
                danger: true,
              }).then((yes) => {
                if (yes) {
                  void act(
                    () => post(`/api/invoices/${inv.id}/discard`),
                    'Draft discarded.',
                    () => nav('/money?tab=invoices'),
                  );
                }
              });
            }}
          >
            Discard this draft
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Sending an invoice kept in this app: print it (or save it as a PDF), then email it to the
 * client with the message already written. The email can't carry the PDF itself, so it is
 * attached by hand; the office then says whether it went, which marks the invoice sent.
 */
function SendIt(props: {
  id: number;
  doc: InvoiceDocument;
  email: { to: string; href: string; body: string } | null;
  draft: boolean;
  onSent(): void;
}) {
  const { doc, email } = props;
  return (
    <section className="panel send-it" aria-labelledby="send-h">
      <h2 id="send-h">{props.draft ? 'Send it to the client' : 'Send it again'}</h2>
      {doc.missing.length > 0 && (
        <p className="notice">
          The invoice has no {doc.missing.join(' or ')} yet.{' '}
          <Link to="/settings?tab=invoicing">Add them in Settings</Link> before you send it.
        </p>
      )}
      <ol className="send-steps">
        <li>
          <span>Save the invoice as a PDF.</span>
          <Link className="button" to={`/invoices/${props.id}/print`}>
            Print or save as PDF
          </Link>
        </li>
        <li>
          <span>Email it to {email ? email.to : doc.bill_to.name}, with the PDF attached.</span>
          {email ? (
            <ComposeButtons
              items={[{ channel: 'email', href: email.href, to: email.to, body: email.body }]}
              onDone={props.draft ? (s) => s === 'sent' && props.onSent() : undefined}
            />
          ) : (
            <span className="muted">
              {doc.bill_to.name} has no email address for invoices yet. Add one on their record.
            </span>
          )}
        </li>
      </ol>
    </section>
  );
}

/**
 * The invoice as the client gets it, laid out for paper: printing it, or saving it as a PDF
 * from the print dialog, gives the file to send. Only the invoice prints, not the app around it.
 */
export function InvoicePrint() {
  const { id } = useParams();
  const loaded = useLoad(() => get<Loaded>(`/api/invoices/${id}`), [id]);
  if (loaded.loading && !loaded.data) return <p>Loading…</p>;
  const data = loaded.data;
  if (!data?.document) {
    return (
      <>
        <p role="alert" className="error">
          {loaded.error || 'This invoice is in Xero: print it from there.'}
        </p>
        <Link to={`/invoices/${id}`}>Back to the invoice</Link>
      </>
    );
  }
  const { invoice: inv, lines, document: doc } = data;
  const noAbn = doc.tax_invoice && !doc.business.abn;
  return (
    // print-blocked: Ctrl+P prints the notice, not a tax invoice without its ABN.
    <div className={noAbn ? 'stack print-blocked' : 'stack'}>
      {doc.missing.length > 0 && (
        <p className="notice no-print">
          {noAbn
            ? 'A tax invoice needs your ABN, so this one can’t be printed until it is set'
            : `This invoice has no ${doc.missing.join(' or ')} yet`}
          . <Link to="/settings?tab=invoicing">Add it in Settings</Link>
          {noAbn && doc.missing.includes('bank details') ? ', with your bank details.' : '.'}
        </p>
      )}
      <div className="row start no-print">
        <Link to={`/invoices/${id}`}>← Invoice {doc.label}</Link>
        {/* A tax invoice must carry the ABN: until it is set, this one can't go out. */}
        <button type="button" disabled={noAbn} onClick={() => window.print()}>
          Print or save as PDF
        </button>
        <span className="muted">
          In the print window, choose Save as PDF to get the file to send.
        </span>
      </div>
      <article className="invoice-paper" aria-label={`Invoice ${doc.label}`}>
        <header className="paper-head">
          <div>
            {doc.business.logo && <img src={doc.business.logo} alt="" className="paper-logo" />}
            <p className="paper-business">{doc.business.name}</p>
            {doc.business.abn ? (
              <p>ABN {doc.business.abn}</p>
            ) : (
              doc.tax_invoice && <p className="paper-gap no-print">ABN not set</p>
            )}
            {doc.business.address && <p className="paper-lines">{doc.business.address}</p>}
            {doc.business.phone && <p>{doc.business.phone}</p>}
          </div>
          <div className="paper-title">
            <h1>{doc.tax_invoice ? 'Tax invoice' : 'Invoice'}</h1>
            <dl>
              <div>
                <dt>Number</dt>
                <dd>{doc.label}</dd>
              </div>
              <div>
                <dt>Date</dt>
                <dd>{doc.issued}</dd>
              </div>
              <div>
                <dt>Due</dt>
                <dd>{doc.due}</dd>
              </div>
              {inv.reference && (
                <div>
                  <dt>Reference</dt>
                  <dd>{inv.reference}</dd>
                </div>
              )}
            </dl>
          </div>
        </header>
        <section className="paper-to">
          <h2>Bill to</h2>
          <p>{doc.bill_to.name}</p>
          {doc.bill_to.address && <p className="paper-lines">{doc.bill_to.address}</p>}
        </section>
        <table className="paper-lines-table">
          <thead>
            <tr>
              <th>Description</th>
              <th className="num">Amount ex GST</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <td>{l.description}</td>
                <td className="num">{money(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Total ex GST</th>
              <td className="num">{money(inv.subtotal)}</td>
            </tr>
            <tr>
              <th>GST</th>
              <td className="num">{money(inv.tax_total)}</td>
            </tr>
            <tr className="grand">
              <th>Amount due</th>
              <td className="num">{money(total(inv))}</td>
            </tr>
          </tfoot>
        </table>
        {!doc.bank && (
          <p className="paper-pay paper-gap no-print">How to pay: no bank details set</p>
        )}
        {doc.bank && (
          <section className="paper-pay">
            <h2>How to pay</h2>
            <p>
              By bank transfer to {doc.bank.name || doc.business.name}, BSB {doc.bank.bsb}, account{' '}
              {doc.bank.account}, quoting {doc.label}, by {doc.due}.
            </p>
          </section>
        )}
      </article>
    </div>
  );
}
